(() => {
  if (customElements.get('dc-interactive-demo')) return;

  const carriers = {
    delta: { name: 'Delta Dental', count: '14 claims', total: '$18,420', rows: [['S. Carter','May 9','$4,820','103d'],['M. Lopez','May 18','$3,110','94d'],['J. Kim','Jun 2','$2,760','79d']] },
    cigna: { name: 'Cigna', count: '8 claims', total: '$9,860', rows: [['A. Reed','Jun 5','$2,840','76d'],['P. Hayes','Jun 14','$2,120','67d'],['L. Morgan','Jun 20','$1,980','61d']] },
    aetna: { name: 'Aetna', count: '5 claims', total: '$6,310', rows: [['C. Brown','Jun 17','$1,980','64d'],['D. Foster','Jun 24','$1,510','57d'],['K. Wilson','Jul 1','$1,260','50d']] }
  };

  const money = (value, cents = false) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0
  }).format(Number(value) || 0);

  class DentalCanvasDemo extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.demo = this.dataset.demo || '';
      this.state = 0;
      this.timers = new Set();
      this.onClick = this.handleClick.bind(this);
      this.onInput = this.handleInput.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('input', this.onInput);
      this.reset(false);
      this.observe();
    }

    disconnectedCallback() {
      this.clearTimers();
      this.observer?.disconnect();
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('input', this.onInput);
      this.ready = false;
    }

    $(selector) { return this.querySelector(selector); }
    $$(selector) { return [...this.querySelectorAll(selector)]; }

    later(fn, delay) {
      const id = setTimeout(() => { this.timers.delete(id); fn(); }, delay);
      this.timers.add(id);
    }

    clearTimers() {
      this.timers.forEach(clearTimeout);
      this.timers.clear();
    }

    observe() {
      if (!('IntersectionObserver' in window)) return this.maybeAutoplay();
      this.observer = new IntersectionObserver(([entry]) => {
        if (entry?.isIntersecting && entry.intersectionRatio >= .45) {
          this.maybeAutoplay();
          this.track('demo_viewed', { source: 'viewport' });
          this.observer.disconnect();
        }
      }, { threshold: [.45] });
      this.observer.observe(this);
    }

    maybeAutoplay() {
      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.dataset.autoplay === 'true' && !reduce && !window.Shopify?.designMode) this.later(() => this.play(true), 500);
    }

    analyticsAllowed() {
      try {
        const privacy = window.Shopify?.customerPrivacy;
        return typeof privacy?.analyticsProcessingAllowed !== 'function' || privacy.analyticsProcessingAllowed();
      } catch (_) { return true; }
    }

    track(name, detail = {}) {
      const payload = { demo: this.demo, section_id: this.dataset.sectionId || '', page_path: location.pathname, ...detail };
      const eventName = `dental_canvas:${name}`;
      if (this.analyticsAllowed() && window.Shopify?.analytics?.publish) {
        Promise.resolve(window.Shopify.analytics.publish(eventName, payload)).catch(() => {});
      }
      this.dispatchEvent(new CustomEvent('dental-canvas:demo-event', { bubbles: true, detail: { event: eventName, ...payload } }));
    }

    handleClick(event) {
      const target = event.target.closest('[data-action], [data-demo-play], [data-demo-reset], [data-carrier], [data-demo-cta]');
      if (!target || !this.contains(target)) return;
      if (target.matches('[data-demo-cta]')) return this.track('demo_cta_clicked', { label: target.textContent.trim().slice(0, 80) });
      if (target.matches('[data-demo-play]')) { this.takeControl('play'); return this.play(false); }
      if (target.matches('[data-demo-reset]')) { this.takeControl('reset'); return this.reset(true); }
      if (target.matches('[data-carrier]')) { this.takeControl('select_carrier'); return this.selectCarrier(target.dataset.carrier, true); }
      if (target.disabled) return;
      this.takeControl(target.dataset.action || 'interaction');
      this.action(target.dataset.action, true);
    }

    handleInput(event) {
      if (event.target.matches('[data-associate-pct], [data-associate-guarantee]')) {
        this.takeControl('associate_terms'); this.updateAssociate(true);
      }
      if (event.target.matches('[data-hygiene-production]')) {
        this.takeControl('hygiene_production'); this.updateHygiene(true);
      }
    }

    takeControl(action) {
      this.userControlled = true;
      this.clearTimers();
      if (!this.interacted) { this.interacted = true; this.track('demo_interacted', { action }); }
    }

    setState(value, action, complete = false, track = true) {
      this.state = Number(value);
      this.dataset.state = String(this.state);
      this.renderState();
      this.syncButtons();
      if (track) this.track('demo_step', { action, step: this.state });
      if (complete) {
        this.track('demo_completed', { action, step: this.state });
        if (this.dataset.loop === 'true' && !this.userControlled) this.later(() => this.play(true), 2600);
      }
    }

    syncButtons() {
      this.$$('[data-requires]').forEach((button) => { button.disabled = Number(button.dataset.requires) !== this.state; });
    }

    renderState() {
      if (this.demo === 'office-communication') {
        const labels = ['Ready to request', 'Doctor check sent', 'Dr. Lee accepted · 2 min', 'Dr. Lee arrived', 'Request resolved'];
        this.setText('[data-office-room-status]', labels[Math.min(this.state, labels.length - 1)]);
      }
      if (this.demo === 'insurance-follow-up') {
        const data = carriers[this.currentCarrier || 'delta'];
        this.setText('[data-insurance-status]', this.state > 0
          ? `Calling ${data.name} · ${data.count.toLowerCase()} ready for follow-up.`
          : 'Ranked by age and dollars still owed.');
      }
      if (this.demo === 'collections-promises') {
        const labels = [
          'One call becomes a tracked commitment—not a sticky note.',
          'Promise saved for Friday · $1,240 expected.',
          'Friday passed with no payment. The promise stays visible.',
          'Broken promise returned to today’s follow-up queue.',
          'Follow-up opened · owner and history preserved.'
        ];
        this.setText('[data-promise-result]', labels[Math.min(this.state, labels.length - 1)]);
      }
      if (this.demo === 'payroll-review') {
        const corrected = this.state >= 2;
        this.setText('[data-payroll-minutes]', corrected ? '0 min' : '96 min');
        this.setText('[data-payroll-unreviewed]', corrected ? '0' : '96');
      }
    }

    action(name, track = true) {
      const map = {
        'office-request': () => this.setState(1, 'doctor_check_requested', false, track),
        'office-way': () => this.setState(2, 'doctor_on_my_way', false, track),
        'office-arrived': () => this.setState(3, 'doctor_arrived', true, track),
        'ar-split': () => this.setState(1, 'ar_split', false, track),
        'ar-drill': () => this.setState(2, 'ar_90_plus_opened', true, track),
        'insurance-call': () => this.setState(1, 'carrier_call_started', true, track),
        'promise-call': () => this.setState(1, 'promise_saved', false, track),
        'promise-due': () => this.setState(2, 'promise_missed', false, track),
        'promise-followup': () => this.setState(3, 'promise_followup_opened', true, track),
        'payroll-review': () => this.setState(1, 'early_punches_reviewed', false, track),
        'payroll-correct': () => this.setState(2, 'approved_punches_corrected', true, track),
        'checkout-send': () => this.setState(1, 'checkout_handoff_sent', false, track),
        'checkout-ack': () => this.setState(2, 'checkout_handoff_acknowledged', false, track),
        'checkout-complete': () => this.setState(3, 'checkout_handoff_completed', true, track)
      };
      map[name]?.();
    }

    reset(track = false) {
      this.clearTimers();
      this.userControlled = false;
      this.state = 0;
      this.dataset.state = '0';
      this.interacted = false;
      const pct = this.$('[data-associate-pct]'); if (pct) pct.value = '30';
      const guarantee = this.$('[data-associate-guarantee]'); if (guarantee) guarantee.value = '1000';
      const production = this.$('[data-hygiene-production]'); if (production) production.value = '2450';
      this.updateAssociate(false);
      this.updateHygiene(false);
      this.selectCarrier('delta', false);
      this.renderState();
      this.syncButtons();
      if (track) this.track('demo_reset', { action: 'restart' });
    }

    play(automatic = false) {
      this.clearTimers();
      this.reset(false);
      this.userControlled = !automatic;
      this.track('demo_played', { source: automatic ? 'autoplay' : 'control' });
      const sequences = {
        'office-communication': [['office-request',350],['office-way',1750],['office-arrived',3100]],
        'ar-clarity': [['ar-split',350],['ar-drill',2100]],
        'insurance-follow-up': [['carrier:cigna',350],['carrier:aetna',1300],['carrier:delta',2250],['insurance-call',3450]],
        'collections-promises': [['promise-call',350],['promise-due',1900],['promise-followup',3300]],
        'payroll-review': [['payroll-review',400],['payroll-correct',2100]],
        'checkout-handoff': [['checkout-send',350],['checkout-ack',1850],['checkout-complete',3200]]
      };
      if (this.demo === 'associate-pay') return this.animateAssociate();
      if (this.demo === 'hygiene-bonus') return this.animateHygiene();
      (sequences[this.demo] || []).forEach(([name, delay]) => this.later(() => {
        if (name.startsWith('carrier:')) this.selectCarrier(name.split(':')[1], false);
        else this.action(name, false);
      }, delay));
    }

    animateAssociate() {
      const pct = this.$('[data-associate-pct]'); const guarantee = this.$('[data-associate-guarantee]');
      if (!pct || !guarantee) return;
      [[25,1250,250],[30,1000,1450],[35,750,2750]].forEach(([p,g,d], i, list) => this.later(() => {
        pct.value = String(p); guarantee.value = String(g); this.updateAssociate(false);
        if (i === list.length - 1) this.setState(1, 'associate_terms_compared', true, false);
      }, d));
    }

    animateHygiene() {
      const input = this.$('[data-hygiene-production]'); if (!input) return;
      [700,1200,1800,2450].forEach((value, i, list) => this.later(() => {
        input.value = String(value); this.updateHygiene(false);
        if (i === list.length - 1) this.setState(1, 'hygiene_threshold_crossed', true, false);
      }, 250 + i * 750));
    }

    updateAssociate(track) {
      const pct = Number(this.$('[data-associate-pct]')?.value || 30);
      const guarantee = Number(this.$('[data-associate-guarantee]')?.value || 1000);
      const commission = 72500 * pct / 100;
      const guaranteeTotal = guarantee * 12;
      const finalPay = Math.max(commission, guaranteeTotal);
      const basis = commission >= guaranteeTotal ? 'Commission wins' : 'Guarantee wins';
      const breakEven = pct > 0 ? guaranteeTotal / (pct / 100) : 0;
      this.setText('[data-associate-pct-out]', `${pct}%`);
      this.setText('[data-associate-guarantee-out]', money(guarantee));
      this.setText('[data-associate-contract]', `Contract (${pct}%)`);
      this.setText('[data-associate-commission]', money(commission));
      this.setText('[data-associate-guarantee-total]', money(guaranteeTotal));
      this.setText('[data-associate-final]', money(finalPay));
      this.setText('[data-associate-basis]', basis);
      this.setText('[data-associate-break-even]', money(breakEven));
      if (track) this.track('demo_step', { action: 'associate_terms_changed', contract_pct: pct, guarantee });
    }

    updateHygiene(track) {
      const production = Number(this.$('[data-hygiene-production]')?.value || 0);
      const overflow = Math.max(0, production - 1200);
      const bonus = overflow * .25;
      this.setText('[data-hygiene-production-out]', money(production));
      this.setText('[data-hygiene-overflow]', money(overflow));
      this.setText('[data-hygiene-bonus]', money(bonus, bonus % 1 !== 0));
      this.setText('[data-hygiene-message]', bonus > 0 ? `Emily earned ${money(bonus, bonus % 1 !== 0)} today.` : 'Production has not crossed the bonus threshold yet.');
      const fill = this.$('[data-hygiene-fill]'); if (fill) fill.style.width = `${Math.min(100, production / 3000 * 100)}%`;
      if (track) this.track('demo_step', { action: 'hygiene_production_changed', production });
    }

    selectCarrier(key, track) {
      const data = carriers[key] || carriers.delta;
      this.currentCarrier = key in carriers ? key : 'delta';
      this.$$('[data-carrier]').forEach((button) => {
        const selected = button.dataset.carrier === this.currentCarrier;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-selected', String(selected));
      });
      this.setText('[data-insurance-name]', data.name);
      this.setText('[data-insurance-count]', data.count);
      this.setText('[data-insurance-total]', data.total);
      const rows = this.$('[data-insurance-rows]');
      if (rows) rows.innerHTML = data.rows.map((r) => `<div><span>${r[0]}</span><span>${r[1]}</span><strong>${r[2]}</strong><em>${r[3]}</em></div>`).join('');
      if (track) this.track('demo_step', { action: 'carrier_selected', carrier_rank: ['delta','cigna','aetna'].indexOf(this.currentCarrier) + 1 });
    }

    setText(selector, value) { const element = this.$(selector); if (element) element.textContent = value; }
  }

  customElements.define('dc-interactive-demo', DentalCanvasDemo);
})();
